import React from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { formatFileSize } from "../../util/numberFormat";
import { ds } from "../../styles/designSystem";

// 내부에서도 쓰고 외부로도 노출 — 재수출이 아니라 import + export 로 나눈다(재수출은 로컬 바인딩 없음).
export { formatFileSize };

const C = ds.colors;


function fileIcon(contentType) {
    if (!contentType) return <InsertDriveFileRoundedIcon />;
    if (contentType.includes("pdf")) return <PictureAsPdfRoundedIcon sx={{ color: "#ef4444" }} />;
    if (contentType.startsWith("image/")) return <ImageRoundedIcon sx={{ color: "#3b82f6" }} />;
    return <InsertDriveFileRoundedIcon sx={{ color: C.textMuted }} />;
}

/** 업로드 대기 파일 칩(작성 전, 아직 서버 미전송). */
export function PendingFileChip({ file, onRemove }) {
    return (
        <Chip
            icon={<InsertDriveFileRoundedIcon sx={{ fontSize: 14 }} />}
            label={file.name}
            size="small"
            onDelete={onRemove}
            sx={{ maxWidth: 220, fontSize: 12, bgcolor: "#f1f5f9", border: "1px solid #e2e8f0" }}
        />
    );
}

function FileCard({ attachment, editable, onDelete }) {
    const inner = (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                p: "7px 11px",
                borderRadius: 1.5,
                border: `1px solid ${C.border}`,
                bgcolor: "#f8fafc",
                maxWidth: 300,
                "&:hover": { bgcolor: "#f1f5f9" },
                transition: "background 0.15s",
            }}
        >
            <Box sx={{ fontSize: 22, display: "flex", flexShrink: 0 }}>{fileIcon(attachment.contentType)}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }} noWrap>
                    {attachment.originalFileName}
                </Typography>
                <Typography sx={{ fontSize: 11, color: C.textMuted }}>
                    {formatFileSize(attachment.fileSize)}
                </Typography>
            </Box>
            {editable ? (
                <IconButton
                    size="small"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onDelete?.(attachment.id); }}
                    sx={{ p: 0.2 }}
                >
                    <CancelRoundedIcon sx={{ fontSize: 15, color: C.textMuted }} />
                </IconButton>
            ) : (
                <DownloadRoundedIcon sx={{ fontSize: 15, color: C.textMuted, flexShrink: 0 }} />
            )}
        </Box>
    );
    if (editable) return inner;
    return (
        <Box
            component="a"
            href={attachment.fileUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{ textDecoration: "none" }}
        >
            {inner}
        </Box>
    );
}

/**
 * 첨부 목록 렌더 — 이미지는 썸네일 그리드, 그 외는 파일 카드.
 * @param editable true면 삭제 버튼(onDelete(id)) 노출, false면 다운로드/새 탭 열기.
 * @param compact true면 카드 목록에서 간결하게(이미지 썸네일만, 최대 폭 축소).
 */
export default function FeedbackAttachmentList({ attachments = [], editable = false, onDelete, compact = false }) {
    if (!attachments.length) return null;
    const images = attachments.filter((a) => a.contentType?.startsWith("image/"));
    const files = attachments.filter((a) => !a.contentType?.startsWith("image/"));

    return (
        <Box sx={{ mt: compact ? 0.75 : 1 }}>
            {images.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: files.length ? 1 : 0 }}>
                    {images.map((a) => (
                        <Box key={a.id} sx={{ position: "relative" }}>
                            <Box
                                component="img"
                                src={a.fileUrl}
                                alt={a.originalFileName}
                                loading="lazy"
                                onClick={(e) => { e.stopPropagation(); if (!editable) window.open(a.fileUrl, "_blank"); }}
                                sx={{
                                    width: compact ? 84 : 120,
                                    height: compact ? 84 : 120,
                                    objectFit: "cover",
                                    borderRadius: 1.5,
                                    border: `1px solid ${C.border}`,
                                    cursor: editable ? "default" : "zoom-in",
                                    display: "block",
                                }}
                            />
                            {editable && (
                                <IconButton
                                    size="small"
                                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onDelete?.(a.id); }}
                                    sx={{
                                        position: "absolute", top: 2, right: 2,
                                        bgcolor: "rgba(15,23,42,0.6)", p: 0.2,
                                        "&:hover": { bgcolor: "rgba(15,23,42,0.8)" },
                                    }}
                                >
                                    <CancelRoundedIcon sx={{ fontSize: 15, color: "#fff" }} />
                                </IconButton>
                            )}
                        </Box>
                    ))}
                </Box>
            )}
            {files.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {files.map((a) => (
                        <FileCard key={a.id} attachment={a} editable={editable} onDelete={onDelete} />
                    ))}
                </Box>
            )}
        </Box>
    );
}
